import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Toolbar, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PurchaseOrder, BulkActionResult } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';

type BulkResultView = {
  verb: string;
  result: BulkActionResult;
};

export const PendingApprovalPurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [resultView, setResultView] = useState<BulkResultView | null>(null);

  const fetchPOs = async (page: number = 0) => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.listByInstitution(page, 10, 'PENDING_APPROVAL');
      setPos(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
      setSelected(new Set());
    } catch (e) {
      toast.showApiError(e, 'Failed to load pending approval purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPOs(page - 1);
  };

  const toggleRow = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = pos.length > 0 && pos.every(po => selected.has(po.id));
  const someOnPageSelected = pos.some(po => selected.has(po.id));

  const toggleAllOnPage = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pos.forEach(po => next.delete(po.id));
      } else {
        pos.forEach(po => next.add(po.id));
      }
      return next;
    });
  };

  const runBulk = async (
    verb: string,
    fn: (ids: string[]) => Promise<BulkActionResult>,
  ) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setSubmitting(true);
    try {
      const result = await fn(ids);
      setResultView({ verb, result });
      toast.showSuccess(`${result.succeeded.length} ${verb}, ${result.failed.length} failed`);
      await fetchPOs(pageInfo.currentPage);
    } catch (e) {
      toast.showApiError(e, `Failed to ${verb.replace(/d$/, '')} purchase orders`);
    } finally {
      setSubmitting(false);
    }
  };

  const poNumberFor = (id: string) => pos.find(p => p.id === id)?.poNumber ?? id;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Pending Approval Purchase Orders</Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {selected.size > 0 && (
        <Toolbar disableGutters sx={{ gap: 2, mb: 1 }}>
          <Typography variant="body2">{selected.size} selected</Typography>
          <Button
            variant="contained"
            color="success"
            disabled={submitting}
            onClick={() => runBulk('approved', PurchaseOrderService.bulkApprove)}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={submitting}
            onClick={() => runBulk('rejected', PurchaseOrderService.bulkReject)}
          >
            Reject
          </Button>
          <Button variant="text" disabled={submitting} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Toolbar>
      )}

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={toggleAllOnPage}
                    inputProps={{ 'aria-label': 'select all on page' }}
                  />
                </TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Supplier ID</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pos.map(po => (
                <TableRow key={po.id} selected={selected.has(po.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(po.id)}
                      onChange={() => toggleRow(po.id)}
                      inputProps={{ 'aria-label': `select ${po.poNumber}` }}
                    />
                  </TableCell>
                  <TableCell>{po.poNumber}</TableCell>
                  <TableCell>{po.supplierId}</TableCell>
                  <TableCell>{po.orderDate}</TableCell>
                  <TableCell>
                    <Chip label={po.status} color="warning" size="small" />
                  </TableCell>
                  <TableCell>{po.totalAmount}</TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        data-testid={`view-po-${po.id}`}
                        onClick={() => navigate(`/procurement/purchase-orders/pending-approval/${po.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog open={resultView !== null} onClose={() => setResultView(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resultView
            ? `${resultView.result.succeeded.length} ${resultView.verb}, ${resultView.result.failed.length} failed`
            : ''}
        </DialogTitle>
        <DialogContent>
          {resultView && resultView.result.failed.length > 0 ? (
            <Stack spacing={1}>
              <DialogContentText>Some purchase orders could not be processed:</DialogContentText>
              {resultView.result.failed.map(f => (
                <Typography key={f.id} variant="body2">
                  {poNumberFor(f.id)} — {f.error}
                </Typography>
              ))}
            </Stack>
          ) : (
            <DialogContentText>All selected purchase orders were processed.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovalPurchaseOrders;
